import React, {useEffect, useRef, useState} from "react";
import {Box, Button, ButtonBase, IconButton, InputBase, MenuItem, Modal, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Idea01Icon, Cancel01Icon, SendIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import aiFlowBuilder from "../../config/axios-ai-flow-builder";
import {MOBILE_BOTTOM_NAV_HEIGHT} from "./MobileBottomNav";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {Flag} from "../../pages/auth/components/CountryCodeSelect";
import {ConversationRepository} from "../../repositories/ConversationRepository";

const CHAT_URL = "/api/chat";
const LANGUAGES = [
  {value: "mk-MK", label: "Macedonian", iso: "MK", greeting: "Здраво, јас сум Brainy. Како можам да ви помогнам со овој разговор?"},
  {value: "en-US", label: "English", iso: "US", greeting: "Hi, I'm Brainy. How can I help you with this conversation?"},
  {value: "fr-FR", label: "French", iso: "FR", greeting: "Bonjour, je suis Brainy. Comment puis-je vous aider avec cette conversation ?"},
];
const languageOf = (value) => LANGUAGES.find(item => item.value === value);
const subProjectLanguage = (subProject) => {
  const code = subProject?.languageCode?.toLowerCase();
  return code ? LANGUAGES.find(item => item.value.toLowerCase() === code || item.value.slice(0, 2) === code.slice(0, 2))?.value || "" : "";
};

const LanguageOption = ({item}) => (
  <Stack direction={"row"} alignItems={"center"} gap={1.25}>
    <Flag iso={item.iso}/>
    <Box component={"span"}>{item.label}</Box>
  </Stack>
);
const LANGUAGE_KEY = "brainyLanguage";

const readStorage = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
};

const writeStorage = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (e) {
    // storage unavailable, the language is just not remembered
  }
};

const CONTEXT_MESSAGES = 8;
const CONTEXT_TEXT_LENGTH = 500;

// The hidden first message that gives Brainy the conversation the user is looking at: its latest messages that are
// finished (last_message true; no debug / hold-music messages, no voice message still being processed), with only the
// first message of each run of bot messages (the rest are follow-up questions). null when there is nothing to say.
const buildContextMessage = (conversationId, messages) => {
  const lines = (messages || [])
    .filter(item => {
      const role = item?.role || item?.sender;
      return (role === "USER" || role === "AGENT") && String(item?.last_message) === "true"
        && String(item?.is_protected) === "true" && item?.text?.trim();
    })
    .sort((a, b) => a.id - b.id)
    .filter((item, index, list) => (item.role || item.sender) !== "AGENT"
      || index === 0 || (list[index - 1].role || list[index - 1].sender) !== "AGENT")
    .slice(-CONTEXT_MESSAGES)
    .map(item => `${(item.role || item.sender) === "USER" ? "Customer" : "Bot"}: ${item.text.trim().slice(0, CONTEXT_TEXT_LENGTH)}`);
  if (lines.length === 0) return null;
  return `I am looking at conversation #${conversationId} in the supervision panel. Its latest messages, oldest first:\n\n`
    + `${lines.join("\n")}\n\nSummarize this conversation in 2-3 short sentences: what the customer wants and where it stands now. `
    + `Then add one line starting with "Mood:" saying whether the customer is calm, confused, frustrated or angry, `
    + `and one line starting with "Suggestion:" saying whether the supervisor should step in or let the bot continue, `
    + `with a short reason. Then ask how you can help.`;
};

const bubbleSx = (own) => ({
  alignSelf: own ? "flex-end" : "flex-start", maxWidth: "85%", px: 1.5, py: 1, borderRadius: "16px",
  bgcolor: own ? "rgba(132, 145, 247, 0.14)" : "#FAFAFD", color: own ? "#4B55C9" : "#23264D",
  fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word",
});

// Debug: the model's reasoning summary, collapsed until clicked
const ReasoningToggle = ({reasoning}) => {
  const [open, setOpen] = useState(false);
  const steps = [].concat(reasoning ?? []).filter(step => String(step ?? "").trim());
  if (steps.length === 0) return null;
  return (
    <Box sx={{alignSelf: "flex-start", maxWidth: "85%", mt: -0.5}}>
      <ButtonBase
        onClick={() => setOpen(current => !current)} aria-expanded={open}
        sx={{
          px: 1.25, py: 0.5, borderRadius: "10px", fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
          color: "#8A8AA0", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
        }}
      >
        {open ? "Hide reasoning" : "Show reasoning"}
      </ButtonBase>
      {open && (
        <Stack gap={0.75} sx={{
          mt: 0.75, px: 1.5, py: 1, borderRadius: "14px", bgcolor: "rgba(132, 145, 247, 0.05)",
          fontSize: 12.5, color: "#6B6B80", whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {steps.map((step, index) => <Box key={index}>{step}</Box>)}
        </Stack>
      )}
    </Box>
  );
};

// Chat with the AI helper: every question is a POST to the flow builder's /api/chat, which returns the reply and
// the conversation id to send with the next question. Fills its parent.
// active: whether it is shown. supervisedId: the supervision conversation the user is looking at; when the chat is
// shown with one selected, its latest messages are sent first as a hidden message and only Brainy's reply is shown.
export const AiHelperChat = ({onClose, active = true, supervisedId = null}) => {
  const {project, subProject} = useRegistryProject();
  // the supervision conversation Brainy was last given
  const primedRef = useRef(null);
  // the language Brainy answers in: asked once, then remembered
  const [language, setLanguage] = useState(readStorage(LANGUAGE_KEY) || subProjectLanguage(subProject));
  const [pickedLanguage, setPickedLanguage] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const submitLanguage = (e) => {
    e?.preventDefault();
    if (!pickedLanguage) return;
    writeStorage(LANGUAGE_KEY, pickedLanguage);
    setLanguage(pickedLanguage);
  };

  const changeLanguage = () => {
    writeStorage(LANGUAGE_KEY, "");
    setPickedLanguage(language);
    setLanguage("");
    setMessages([]);
    setConversationId(null);
    primedRef.current = null;
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
  }, [messages, sending]);

  // silent: the reply is not shown (the hidden context message)
  const ask = async (message, {silent = false} = {}) => {
    const {data} = await aiFlowBuilder.post(CHAT_URL, {
      message,
      language,
      conversation_id: conversationId,
      project_id: project?.id ? String(project.id) : null,
    });
    setConversationId(data.conversation_id);
    if (!silent) setMessages(current => [...current, {text: data.reply || "No answer found.", reasoning: data.reasoning}]);
  };

  const send = async (e) => {
    e?.preventDefault();
    const query = text.trim();
    if (!query || sending) return;
    setText("");
    setMessages(current => [...current, {own: true, text: query}]);
    setSending(true);
    try {
      await ask(query);
    } catch (err) {
      toast.error("Query failed");
    } finally {
      setSending(false);
    }
  };

  // Hidden first message: once per supervision conversation, when the chat is shown and a language is chosen
  useEffect(() => {
    if (!active || !language || !supervisedId || sending || primedRef.current === supervisedId) return;
    primedRef.current = supervisedId;
    (async () => {
      setSending(true);
      try {
        const [conversation, history] = await Promise.all([
          ConversationRepository.getConversation({id: supervisedId}),
          ConversationRepository.getMessages(supervisedId),
        ]);
        // only a live conversation is worth priming Brainy with
        if (conversation?.data?.status !== "ACTIVE") return;
        const context = buildContextMessage(supervisedId, history.data);
        if (context) await ask(context);
      } catch (err) {
        // Brainy still works without the context
        console.error("Failed to give Brainy the conversation", err);
      } finally {
        setSending(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, language, supervisedId, sending]);

  return (
    <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden"}}>
        <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{px: 2.5, py: 1.75}}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9",
          }}>
            <HugeiconsIcon icon={Idea01Icon} size={20}/>
          </Box>
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>
              AI helper
            </Typography>
            <Typography id={"ai-helper-title"} sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>Brainy</Typography>
          </Box>
          {language && (
            <ButtonBase
              onClick={changeLanguage} aria-label={"Change language"}
              sx={{px: 1.25, py: 0.5, borderRadius: "10px", fontSize: 12, fontWeight: 600, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.22)"}}}
            >
              {languageOf(language) ? <LanguageOption item={languageOf(language)}/> : language}
            </ButtonBase>
          )}
          <IconButton onClick={onClose} aria-label={"Close"} sx={{bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"}}}>
            <HugeiconsIcon icon={Cancel01Icon} size={18}/>
          </IconButton>
        </Stack>

        {!language ? (
          <Stack component={"form"} onSubmit={submitLanguage} justifyContent={"center"} sx={{flex: 1, minHeight: 0, p: 2.5}}>
            <Box sx={{p: 3, bgcolor: "#FAFAFD", borderRadius: "18px"}}>
              <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>Choose a language</Typography>
              <Typography sx={{mt: 0.5, fontSize: 13, color: "#8A8AA0", lineHeight: 1.5}}>
                Brainy answers in this language. It is remembered for your next chats, and you can change it anytime.
              </Typography>
              <Select
                fullWidth size={"small"} displayEmpty value={pickedLanguage} onChange={(e) => setPickedLanguage(e.target.value)}
                renderValue={(value) => languageOf(value)
                  ? <LanguageOption item={languageOf(value)}/>
                  : <Box component={"span"} sx={{color: "#8A8AA0"}}>Select a language</Box>}
                MenuProps={{
                  // above the Brainy modal on mobile (it sits over the conversation view)
                  sx: {zIndex: 1500},
                  PaperProps: {sx: {mt: 0.75, p: 0.5, borderRadius: "14px", boxShadow: "0 12px 32px rgba(35, 38, 77, 0.14)"}},
                  MenuListProps: {sx: {p: 0}},
                }}
                sx={{mt: 2.5, borderRadius: "12px", bgcolor: "#fff", fontSize: 14, color: "#23264D", "& fieldset": {border: "none"}}}
              >
                {LANGUAGES.map(item => (
                  <MenuItem key={item.value} value={item.value} sx={{
                    borderRadius: "10px", py: 1, fontSize: 14, color: "#23264D",
                    "&.Mui-selected": {bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9"},
                    "&.Mui-selected:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
                  }}>
                    <LanguageOption item={item}/>
                  </MenuItem>
                ))}
              </Select>
              <Button
                type={"submit"} fullWidth disabled={!pickedLanguage}
                sx={{
                  mt: 2, height: 40, borderRadius: "10px", textTransform: "none", fontSize: 14, fontWeight: 600,
                  color: "#374151", bgcolor: "#fff", "&:hover": {bgcolor: "#ECECF3"}, "&.Mui-disabled": {color: "#B4B4C3", bgcolor: "#fff"},
                }}
              >
                Continue
              </Button>
            </Box>
          </Stack>
        ) : (<>
        <Stack gap={1.25} sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 1.5}}>
          {languageOf(language)?.greeting && <Box sx={bubbleSx(false)}>{languageOf(language).greeting}</Box>}
          {messages.map((message, index) => (
            <React.Fragment key={index}>
              <Box sx={bubbleSx(message.own)}>{message.text}</Box>
              {!message.own && <ReasoningToggle reasoning={message.reasoning}/>}
            </React.Fragment>
          ))}
          {sending && (
            <Box sx={{...bubbleSx(false), display: "flex", gap: 0.6, alignItems: "center", py: 1.5}}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{
                  width: 6, height: 6, borderRadius: "50%", bgcolor: "#8491F7",
                  animation: "brainy-typing 1.2s ease-in-out infinite", animationDelay: `${i * 0.16}s`,
                  "@keyframes brainy-typing": {
                    "0%, 60%, 100%": {transform: "translateY(0)", opacity: 0.4},
                    "30%": {transform: "translateY(-4px)", opacity: 1},
                  },
                }}/>
              ))}
            </Box>
          )}
          <Box ref={endRef}/>
        </Stack>

        <Stack component={"form"} onSubmit={send} direction={"row"} alignItems={"center"} gap={1} sx={{p: 2}}>
          <InputBase
            fullWidth value={text} onChange={(e) => setText(e.target.value)}
            placeholder={"Ask a question..."} inputProps={{"aria-label": "Question"}}
            sx={{height: 44, px: 1.75, borderRadius: "14px", bgcolor: "#FAFAFD", fontSize: 14, color: "#23264D"}}
          />
          <IconButton
            type={"submit"} disabled={!text.trim() || sending} aria-label={"Send"}
            sx={{
              width: 44, height: 44, borderRadius: "14px", color: "#fff", bgcolor: "#8491F7", "&:hover": {bgcolor: "#6B78E8"},
              "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"},
            }}
          >
            <HugeiconsIcon icon={SendIcon} size={18}/>
          </IconButton>
        </Stack>
        </>)}
    </Box>
  );
};

// Mobile: rounded floating modal above the bottom navigation, kept mounted so the conversation survives closing.
export const AiHelperModal = ({open, onClose, zIndex = 1050, supervisedId = null}) => (
  <Modal open={open} onClose={onClose} keepMounted aria-labelledby={"ai-helper-title"} sx={{zIndex}}>
    <Box sx={{
      position: "absolute", top: 12, left: 12, width: "calc(100% - 24px)",
      height: `calc(100% - ${MOBILE_BOTTOM_NAV_HEIGHT + 28}px)`,
      display: "flex", flexDirection: "column", overflow: "hidden", outline: "none",
      bgcolor: "#fff", borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)",
    }}>
      <AiHelperChat key={supervisedId} active={open} onClose={onClose} supervisedId={supervisedId}/>
    </Box>
  </Modal>
);
